import { useState, useCallback, useMemo } from 'react'
import {
    CalendarView,
    getMonthDays,
    getWeekDays,
    navigateDate,
    getDateRangeForView,
    formatMonthYear,
    formatDate,
} from '../utils/calendar-utils'
import { startOfWeek, endOfWeek, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export interface UseCalendarOptions {
    initialDate?: Date
    initialView?: CalendarView
}

export interface UseCalendarReturn {
    currentDate: Date
    view: CalendarView
    days: ReturnType<typeof getMonthDays>
    dateRange: { start: Date; end: Date }
    headerTitle: string
    setCurrentDate: (date: Date) => void
    setView: (view: CalendarView) => void
    goToToday: () => void
    goToPrevious: () => void
    goToNext: () => void
    goToDate: (date: Date) => void
}

export function useCalendar(options: UseCalendarOptions = {}): UseCalendarReturn {
    const { initialDate = new Date(), initialView = 'month' } = options

    const [currentDate, setCurrentDate] = useState<Date>(initialDate)
    const [view, setView] = useState<CalendarView>(initialView)

    const days = useMemo(() => {
        if (view === 'month') {
            return getMonthDays(currentDate)
        } else if (view === 'week') {
            return getWeekDays(currentDate)
        }
        return getWeekDays(currentDate).filter(
            d => d.date.toDateString() === currentDate.toDateString()
        )
    }, [currentDate, view])

    const dateRange = useMemo(() => {
        return getDateRangeForView(currentDate, view)
    }, [currentDate, view])

    const headerTitle = useMemo(() => {
        if (view === 'month') {
            return formatMonthYear(currentDate)
        } else if (view === 'week') {
            const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
            const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
            return `${format(weekStart, 'dd', { locale: ptBR })} - ${format(
                weekEnd,
                'dd MMM yyyy',
                { locale: ptBR }
            )}`
        }
        return format(currentDate, "EEEE, dd 'de' MMMM", { locale: ptBR })
    }, [currentDate, view])

    const goToToday = useCallback(() => {
        setCurrentDate(new Date())
    }, [])

    const goToPrevious = useCallback(() => {
        setCurrentDate(prev => navigateDate(prev, view, 'prev'))
    }, [view])

    const goToNext = useCallback(() => {
        setCurrentDate(prev => navigateDate(prev, view, 'next'))
    }, [view])

    const goToDate = useCallback((date: Date) => {
        setCurrentDate(date)
    }, [])

    return {
        currentDate,
        view,
        days,
        dateRange,
        headerTitle,
        setCurrentDate,
        setView,
        goToToday,
        goToPrevious,
        goToNext,
        goToDate,
    }
}
